import { useContext, useEffect, useState } from "react";
import { Box, Text } from "@mantine/core";
import IdeaContext from "../IdeaContext";
import DiscoveryContext from "../../Discovery/DiscoveryContext";
import { calculateImpactResults, getNestedFieldValue } from "../helpers/calculateImpactResults";
import ImpactList from "./ImpactList";
import { useFields } from "../../../hooks/useFields";

interface Company {
  _id: string;
  arr?: number;
  name: string;
}

interface Chat {
  _id: string;
  itemType?: string;
  amount?: number;
}

interface Request {
  company?: Company | null;
  chat?: Chat | null;
}

const Impact = ({ ideaPage }: { ideaPage: boolean }) => {
  
  const { impactAggregationProperties } = useFields();

  const ideaContext = useContext(IdeaContext);
  const discoveryContext = useContext(DiscoveryContext);
  
  // Get properties based on which context we're using
  const ideaId = ideaPage ? ideaContext.ideaId : discoveryContext.ideaId;
  const idea = ideaPage ? ideaContext.idea : discoveryContext.idea;
  const showmatches = ideaPage ? ideaContext.showmatches : discoveryContext.showmatches;
  const showValidated = ideaPage ? ideaContext.showValidated : discoveryContext.showValidated;
  const activeInboxTab = ideaPage ? ideaContext.activeInboxTab : false;
  
  // Get the correct match variables based on context
  const displayedStrongMatches = ideaPage ? (ideaContext.displayedStrongMatches || []) : (discoveryContext.displayedStrongMatches || []);
  const displayedStrongRequestsMatches = ideaPage ? (ideaContext.displayedStrongRequestsMatches || []) : (discoveryContext.displayedStrongRequestsMatches || []);
  
  // Only get displayedRequests when we actually need it (ideaPage === true)
  const displayedRequests = ideaPage ? ideaContext.displayedRequests : null;

   
 

 

  const [impactData, setImpactData] = useState<Record<string, any>>({});
  const [dynamicCount, setDynamicCount] = useState<number>(0);
  const [dynamicOrgIds, setDynamicOrgIds] = useState<string[]>([]);
 

  useEffect(() => {

    const _pendingRequests = ideaPage && displayedRequests ? displayedRequests.filter(
      (r: any) => r.state === "inReview" 
    ) : []
    const _validatedRequests = ideaPage && displayedRequests ? displayedRequests.filter(
      (r: any) => (r.type === 'gap' && r.ideaId)  || (r.state === 'validated')
    ) : [];

    const matchesCopy = !ideaPage ? [...displayedStrongMatches , ...displayedStrongRequestsMatches  ] : activeInboxTab
      ? [   ..._pendingRequests,  ...displayedStrongRequestsMatches ]
      : (showmatches && !showValidated)
        ? [...displayedStrongMatches]
        : (showmatches && showValidated)
          ? [
            ..._pendingRequests,
            ...displayedStrongMatches,
            ...displayedStrongRequestsMatches
          ]
          : [];


    const validatedCopy = showValidated ? [..._validatedRequests] : [];

    const validatedDeepCopy = structuredClone(validatedCopy);
    const matchesDeepCopy = structuredClone(matchesCopy);

    const companiesIds = new Set<string>();

    validatedDeepCopy.forEach(item => {
      const companyId = item.company?._id;

      if (companyId) {
        if (companyId === 'N/A') {
          item.company = null;
        }
        if (companiesIds.has(companyId)) {
          item.company = null;
        } else {
          companiesIds.add(companyId);
        }
      }
    });

    matchesDeepCopy.forEach(item => {
      const companyId = item.company?._id;

      if (companyId) {
        if (companyId === 'N/A') {
          item.company = null;
        }
        if (companiesIds.has(companyId)) {
          item.company = null;
        } else {
          companiesIds.add(companyId);
        }
      }
    });

    // Calculate dynamic count and org IDs only from strong matches
    const strongMatchesOnly = [...displayedStrongMatches, ...displayedStrongRequestsMatches];
    const strongMatchesCompanyIds = new Set<string>();
    
    strongMatchesOnly.forEach(item => {
      const companyId = getNestedFieldValue(item, "company._id");
      if (companyId && companyId !== 'N/A') {
        strongMatchesCompanyIds.add(companyId);
      }
    });

    setDynamicCount(companiesIds.size);
    setDynamicOrgIds(Array.from(companiesIds));

   
    const impactResults = calculateImpactResults(validatedDeepCopy, matchesDeepCopy, impactAggregationProperties, idea);
 
    setImpactData(impactResults);
    
    // Debug strong matches filtering
  }, [
    displayedRequests, 
    ideaId, 
    showmatches, 
    showValidated,
    activeInboxTab, 
    displayedStrongMatches, 
    displayedStrongRequestsMatches 
  ]);

  return (
    <Box px={6} py={24}>
      <ImpactList
        impactAggregationProperties={impactAggregationProperties}
        impactData={impactData}
        dynamicCount={dynamicCount}
        dynamicOrgIds={dynamicOrgIds}
        idea={idea}
        ideaPage={ideaPage}
      />
    </Box>
  );
};

export default Impact;